import { useState } from "react";
import axios from "axios";
import { CheckCircle2, ChevronDown, Loader2 } from "lucide-react";
import { Reveal, ChapterTag } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INTERESTS = [
  "Manufacturing",
  "Licensing",
  "Product development",
  "Retail & distribution",
  "Something else",
];

const inputCls =
  "w-full rounded-2xl border-2 border-[#EADCC6] bg-white px-5 py-3.5 text-base text-[#243C39] placeholder-[#506460] transition-colors focus:border-[#F3802A] focus:outline-none";

export const Partner = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", interest: INTERESTS[0], message: "" });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [state, setState] = useState("idle");

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    const next = {};
    if (!form.name.trim()) next.name = "Please tell us your name.";
    if (!EMAIL_RE.test(form.email.trim())) next.email = "Please enter a valid email address.";
    if (!form.message.trim()) next.message = "Please add a short message so we know how to help.";
    setErrors(next);
    if (Object.keys(next).length) return;
    setState("submitting");
    try {
      await axios.post(`${API}/partner`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        interest: form.interest,
        message: form.message.trim(),
      });
      setState("success");
    } catch (err) {
      setState("idle");
      setSubmitError("Something went wrong while sending your inquiry. Please try again.");
    }
  };

  return (
    <section id="partner" data-testid="partner-section" className="scroll-mt-24 bg-[#EADCC6] py-20 sm:py-28 lg:py-32">
      <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.15fr] lg:gap-16 lg:px-8">
        <div className="lg:sticky lg:top-32">
          <Reveal>
            <ChapterTag number="05" label="Partners" />
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 text-[1.9rem] font-extrabold leading-tight tracking-tight text-[#074345] sm:text-4xl lg:text-[2.6rem]">
              Interested in helping bring Peanut Valet to market?
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-5 max-w-md text-base leading-relaxed text-[#243C39] sm:text-lg">
              We welcome conversations with potential manufacturing, licensing and
              development partners.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1} y={36}>
          {state === "success" ? (
            <div
              data-testid="partner-success-message"
              role="status"
              className="rounded-[2rem] bg-[#F7F1E6] p-8 shadow-[0_30px_60px_-35px_rgba(7,67,69,0.4)] sm:p-10"
            >
              <div className="flex items-start gap-4">
                <CheckCircle2 className="mt-0.5 h-7 w-7 shrink-0 text-[#F3802A]" aria-hidden="true" />
                <div>
                  <p className="text-lg font-extrabold text-[#074345]">Thanks — we've received your inquiry.</p>
                  <p className="mt-1 text-base text-[#243C39]">
                    We'll be in touch soon to continue the conversation.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <form
              data-testid="partner-form"
              onSubmit={submit}
              noValidate
              className="rounded-[2rem] bg-[#F7F1E6] p-6 shadow-[0_30px_60px_-35px_rgba(7,67,69,0.4)] sm:p-10"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="partner-name" className="mb-2 block text-sm font-bold text-[#074345]">
                    Name <span className="text-[#F3802A]">*</span>
                  </label>
                  <input
                    id="partner-name"
                    data-testid="partner-name-input"
                    type="text"
                    required
                    aria-required="true"
                    autoComplete="name"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                    aria-invalid={errors.name ? "true" : undefined}
                    aria-describedby={errors.name ? "partner-name-error" : undefined}
                    className={inputCls}
                  />
                  {errors.name && (
                    <p id="partner-name-error" data-testid="partner-name-error" role="alert" className="mt-2 text-sm font-bold text-[#B3400F]">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="partner-email" className="mb-2 block text-sm font-bold text-[#074345]">
                    Email <span className="text-[#F3802A]">*</span>
                  </label>
                  <input
                    id="partner-email"
                    data-testid="partner-email-input"
                    type="email"
                    required
                    aria-required="true"
                    autoComplete="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@company.com"
                    aria-invalid={errors.email ? "true" : undefined}
                    aria-describedby={errors.email ? "partner-email-error" : undefined}
                    className={inputCls}
                  />
                  {errors.email && (
                    <p id="partner-email-error" data-testid="partner-email-error" role="alert" className="mt-2 text-sm font-bold text-[#B3400F]">{errors.email}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="partner-company" className="mb-2 block text-sm font-bold text-[#074345]">
                    Company <span className="font-semibold text-[#506460]">(optional)</span>
                  </label>
                  <input id="partner-company" data-testid="partner-company-input" type="text" autoComplete="organization" value={form.company} onChange={set("company")} placeholder="Company or organization" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="partner-interest" className="mb-2 block text-sm font-bold text-[#074345]">
                    Area of interest <span className="text-[#F3802A]">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="partner-interest"
                      data-testid="partner-interest-select"
                      required
                      aria-required="true"
                      value={form.interest}
                      onChange={set("interest")}
                      className={`${inputCls} appearance-none pr-12`}
                    >
                      {INTERESTS.map((i) => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#506460]" aria-hidden="true" />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="partner-message" className="mb-2 block text-sm font-bold text-[#074345]">
                    Message <span className="text-[#F3802A]">*</span>
                  </label>
                  <textarea
                    id="partner-message"
                    data-testid="partner-message-input"
                    rows={5}
                    required
                    aria-required="true"
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Tell us a little about your interest in Peanut Valet."
                    aria-invalid={errors.message ? "true" : undefined}
                    aria-describedby={errors.message ? "partner-message-error" : undefined}
                    className={`${inputCls} resize-y rounded-3xl`}
                  />
                  {errors.message && (
                    <p id="partner-message-error" data-testid="partner-message-error" role="alert" className="mt-2 text-sm font-bold text-[#B3400F]">{errors.message}</p>
                  )}
                </div>
              </div>
              {submitError && (
                <p data-testid="partner-error-message" role="alert" className="mt-4 text-sm font-bold text-[#B3400F]">
                  {submitError}
                </p>
              )}
              <button
                type="submit"
                data-testid="partner-submit-button"
                disabled={state === "submitting"}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#F3802A] px-8 py-4 text-base font-extrabold text-[#043436] transition-[transform,filter] duration-200 hover:scale-[1.02] hover:brightness-95 active:scale-[0.98] disabled:opacity-70"
              >
                {state === "submitting" && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
                Partner With Us
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
